<template>
  <div ref="triggerRef" class="l-tooltip z-index-[1000]" @mouseenter="showTooltip" @mouseleave="hideTooltip" @focusin="showTooltip" @focusout="hideTooltip">
    <slot></slot>
  </div>

  <Teleport to="body">
    <div v-if="visible" ref="tooltipRef" class="tooltip-label" :class="{ down: actualDown }" :style="tooltipStyle">{{ label }}</div>
  </Teleport>
</template>

<script setup>
import { nextTick, onBeforeUnmount, ref, watch } from 'vue'

const props = defineProps({
  label: String,
  color: {
    type: String,
    default: '#0d6efd',
  },
  active: {
    type: Boolean,
    default: false,
  },
  down: {
    type: Boolean,
    default: false,
  },
  size: String,
})

const visible = ref(false)
const triggerRef = ref(null)
const tooltipRef = ref(null)
const tooltipStyle = ref({})
const actualDown = ref(false)

const clamp = (value, min, max) => {
  return Math.min(Math.max(value, min), max)
}

const updatePosition = async () => {
  if (!visible.value || !triggerRef.value) return

  await nextTick()

  if (!tooltipRef.value) return

  const triggerRect = triggerRef.value.getBoundingClientRect()
  const tooltipRect = tooltipRef.value.getBoundingClientRect()
  const offset = 12
  const viewportPadding = 8

  let useDown = !!props.down
  let top = useDown ? triggerRect.bottom + offset : triggerRect.top - tooltipRect.height - offset

  if (!useDown && top < viewportPadding) {
    useDown = true
    top = triggerRect.bottom + offset
  }

  if (useDown && top + tooltipRect.height > window.innerHeight - viewportPadding) {
    useDown = false
    top = triggerRect.top - tooltipRect.height - offset
  }

  let left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2
  left = clamp(left, viewportPadding, window.innerWidth - tooltipRect.width - viewportPadding)
  top = clamp(top, viewportPadding, window.innerHeight - tooltipRect.height - viewportPadding)

  actualDown.value = useDown
  tooltipStyle.value = {
    top: `${top}px`,
    left: `${left}px`,
    minWidth: props.size || undefined,
    '--main-bg-color': props.color,
  }
}

const handleViewportChange = async () => {
  await updatePosition()
}

const toggleViewportListeners = (enabled) => {
  const method = enabled ? 'addEventListener' : 'removeEventListener'
  window[method]('scroll', handleViewportChange, true)
  window[method]('resize', handleViewportChange)
}

const showTooltip = async () => {
  if (!props.label) return
  visible.value = true
  toggleViewportListeners(true)
  await updatePosition()
}

const hideTooltip = () => {
  if (props.active) return
  visible.value = false
  toggleViewportListeners(false)
}

watch(
  () => props.active,
  async (active) => {
    if (active) {
      visible.value = true
      toggleViewportListeners(true)
      await updatePosition()
      return
    }

    if (!active) {
      visible.value = false
      toggleViewportListeners(false)
    }
  },
  { immediate: true },
)

watch(
  () => [props.label, props.color, props.size, props.down],
  async () => {
    if (!visible.value) return
    await updatePosition()
  },
)

onBeforeUnmount(() => {
  toggleViewportListeners(false)
})
</script>

<style>
.l-tooltip {
  display: inline-block;
  position: relative;
}

.tooltip-label {
  display: block;
  position: fixed;
  font-size: 12px;
  text-align: left;

  max-width: 240px;
  border-radius: 5px;
  padding: 5px 8px;
  z-index: 10000;
  color: white;
  background: var(--main-bg-color);
  overflow-wrap: break-word;
  pointer-events: none;
}

.tooltip-label:before {
  content: '';
  position: absolute;
  top: calc(100%);
  left: 50%;
  transform: translateX(-50%);
  border-style: solid;
  border-width: 10px;
  border-color: var(--main-bg-color) transparent transparent transparent;
}

.tooltip-label.down {
  bottom: auto;
}

.tooltip-label.down:before {
  top: -20px;
  left: 50%;
  transform: translateX(-50%);
  border-color: transparent transparent var(--main-bg-color) transparent;
}
</style>
